import React, { useEffect, useRef, useState } from "react";

const legacyLeft = [
  {
    year: "1997",
    title: "Trust-led launch",
    description:
      "Microfinance began as a community trust, lending to women’s collectives across Karnataka.",
    company: "bss",
  },
  {
    year: "2007",
    title: "1 lakh borrowers",
    description:
      "Field teams crossed the one-lakh customer mark through village service centres and mentoring.",
    company: "bss",
  },
  {
    year: "2008",
    title: "NBFC transition",
    description:
      "Operations moved from the trust vehicle to an RBI-regulated NBFC, laying the foundation for scale.",
    company: "bss",
  },
  {
    year: "2014",
    title: "NBFC-MFI recognition",
    description:
      "BSS earned the NBFC-MFI tag, aligning governance, capital and customer protection standards.",
    company: "bss",
  },
  {
    year: "2017",
    title: "Kotak Mahindra Bank takeover",
    description:
      "Kotak acquired BSS as a wholly owned subsidiary; AUM simultaneously crossed ₹1,000 crore.",
    company: "bss",
  },
  {
    year: "2021",
    title: "₹2,000 crore AUM",
    description:
      "Portfolio doubled within two years on the back of analytics-led underwriting.",
    company: "bss",
  },
  {
    year: "2022",
    title: "Silver Jubilee",
    description:
      "Celebrated 25 years with one million borrowers and ₹5,000 crore AUM.",
    company: "bss",
  },
];

const legacyRight = [
  {
    year: "2006",
    title: "Sonata Finance is born",
    description:
      "Sonata set up as a private limited company to serve women entrepreneurs in UP and MP.",
    company: "sonata",
  },
  {
    year: "2008",
    title: "Registered as an NBFC",
    description:
      "RBI registration ensured dedicated capital for scaling the livelihood portfolio.",
    company: "sonata",
  },
  {
    year: "2013",
    title: "NBFC-MFI upgrade",
    description:
      "Converted to the NBFC-MFI category with customer-protection frameworks and village-level processes.",
    company: "sonata",
  },
  {
    year: "2016",
    title: "₹1,000 crore AUM",
    description:
      "Rapid growth across Tier-III towns pushed Sonata beyond the ₹1,000 crore portfolio mark.",
    company: "sonata",
  },
  {
    year: "2023",
    title: "₹2,000 crore platform",
    description:
      "Digitised collections and agri-linked products doubled managed assets again.",
    company: "sonata",
  },
  {
    year: "2024",
    title: "Kotak Mahindra Bank takeover",
    description:
      "Kotak acquired Sonata as a wholly owned subsidiary to mirror the BSS operating model.",
    company: "sonata",
  },
  {
    year: "2025",
    title: "Merged with BSS",
    description:
      "The two legacies fully combined to become BSS Sonata Microcredit.",
    company: "sonata",
  },
];

const mergedMilestones = [
  {
    year: "2025",
    title: "Merged into BSS Sonata Microcredit",
    description:
      "Kotak completed the post-acquisition integration, folding Sonata’s & BSS's teams and processes into the BSS Sonata Microcredit platform.",
    company: "merged",
    highlight: true,
  },
];

const mobileTimeline = [
  ...legacyLeft,
  ...legacyRight,
  ...mergedMilestones,
].sort((a, b) => Number(a.year) - Number(b.year));

const companyStyles = {
  bss: {
    tag: "BSS Microfinance",
    border: "border-[#284B7A]/50",
    text: "text-[#284B7A]",
    bg: "bg-[#284B7A]/10",
    line: "bg-[#284B7A]/30",
    dot: "bg-[#284B7A]",
  },
  sonata: {
    tag: "Sonata Finance",
    border: "border-[#8C6B4A]/50",
    text: "text-[#8C6B4A]",
    bg: "bg-[#8C6B4A]/10",
    line: "bg-[#8C6B4A]/30",
    dot: "bg-[#8C6B4A]",
  },
  merged: {
    tag: "BSS Sonata Microcredit",
    border: "border-[#1c3d72]",
    text: "text-[#1c3d72]",
    bg: "bg-[#1c3d72]/10",
    line: "bg-[#1c3d72]",
    dot: "bg-[#1c3d72]",
  },
};

const Tag = ({ company }) => {
  const styles = companyStyles[company];
  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-[11px] font-semibold ${styles.border} ${styles.text}`}
    >
      {styles.tag}
    </span>
  );
};

const TimelineCard = ({ item, delay = 0, isVisible }) => {
  return (
    <div
      className={`rounded-[32px] bg-white3 shadow-[0_40px_120px_rgba(23,23,23,0.08)] p-6 space-y-3 transition-all duration-500 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs tracking-[0.3em] uppercase text-black/60">
          {item.year}
        </span>
        <Tag company={item.company} />
      </div>
      <p className="text-lg font-semibold text-black/90">{item.title}</p>
      <p className="text-sm text-black/70 leading-relaxed">
        {item.description}
      </p>
    </div>
  );
};

const MobileTimeline = ({ isVisible, items = mobileTimeline }) => {
  return (
    <div className="md:hidden relative pl-6">
      <span className="absolute left-2 top-0 bottom-0 w-px bg-[#1c3d72]/20" />
      <div className="space-y-8">
        {items.map((item, index) => {
          const styles = companyStyles[item.company];
          return (
            <div key={`${item.year}-${index}`} className="relative">
              <span
                className={`absolute left-0 top-2 w-4 h-4 rounded-full ring-4 ring-white3 ${
                  styles.dot
                } transition-all duration-500 ${
                  isVisible ? "opacity-100 scale-100" : "opacity-0 scale-50"
                }`}
                style={{ transitionDelay: `${index * 80}ms` }}
              />
              <TimelineCard
                item={item}
                isVisible={isVisible}
                delay={index * 80}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function OurJourney({ compactTop = false }) {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const collapsedBssCount = 3;
  const collapsedSonataCount = 2;
  const collapsedMergedCount = 2;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const displayedLegacyLeft = expanded
    ? legacyLeft
    : legacyLeft.slice(0, collapsedBssCount);
  const displayedLegacyRight = expanded
    ? legacyRight
    : legacyRight.slice(0, collapsedSonataCount);
  const displayedMergedMilestones = expanded
    ? mergedMilestones
    : mergedMilestones.slice(0, collapsedMergedCount);
  const displayedMobileTimeline = expanded
    ? mobileTimeline
    : [
        ...displayedLegacyLeft,
        ...displayedLegacyRight,
        ...displayedMergedMilestones,
      ].sort((a, b) => Number(a.year) - Number(b.year));
  const highlightMilestone = mergedMilestones[0] || null;
  const sectionPadding = compactTop ? "py-8 sm:py-12" : "py-16 sm:py-20";

  return (
    <section
      ref={sectionRef}
      className={`bg-ofWhite ${sectionPadding} px-4 overflow-hidden`}
    >
      <div className="max-w-6xl mx-auto space-y-10">
        <div className="text-center space-y-3">
          <p className="text-xs tracking-[0.4em] uppercase text-black/50">
            Our Journey
          </p>
          <h2 className="text-3xl md:text-4xl font-semibold text-black/85">
            Two legacies converging into one, people-first institution
          </h2>
          <p className="text-black/60 max-w-3xl mx-auto">
            A dual timeline celebrates the individual strengths of BSS
            Microfinance and Sonata Finance before they unify into BSS Sonata
            Microcredit.
          </p>
        </div>

        <div
          className={`relative transition-[max-height] duration-500 ease-in-out ${
            expanded
              ? "max-h-[5000px]"
              : "max-h-[780px] sm:max-h-[880px] md:max-h-[960px] overflow-hidden"
          }`}
        >
          {!expanded && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent via-ofWhite/70 to-ofWhite" />
          )}
          <div className="space-y-10">
            {/* Desktop */}
            <div className="hidden md:block relative">
              <div className="grid grid-cols-[1fr_auto_1fr] gap-8 relative pb-18">
                <div className="relative flex flex-col gap-10 pr-6 pt-0">
                  <span className="absolute top-0 right-0 bottom-[-120px] w-px bg-[#284B7A]/30" />
                  {displayedLegacyLeft.map((item, index) => {
                    const styles = companyStyles[item.company];
                    return (
                      <div key={item.year} className="relative">
                        <TimelineCard
                          item={item}
                          align="right"
                          delay={index * 120}
                          isVisible={visible}
                        />
                        <span
                          className={`absolute right-[-22px] top-1/2 -translate-y-1/2 w-6 h-px ${styles.line}`}
                        />
                        <span
                          className={`absolute right-[-28px] top-1/2 -translate-y-1/2 w-4 h-4 rounded-full ${styles.dot}`}
                        />
                      </div>
                    );
                  })}
                </div>

                <div aria-hidden="true" />

                <div className="relative flex flex-col gap-10 pl-6 mt-16">
                  <span className="absolute top-0 left-0 bottom-[-120px] w-px bg-[#8C6B4A]/30" />
                  {displayedLegacyRight.map((item, index) => {
                    const styles = companyStyles[item.company];
                    return (
                      <div key={item.year} className="relative">
                        <TimelineCard
                          item={item}
                          align="left"
                          delay={index * 120}
                          isVisible={visible}
                        />
                        <span
                          className={`absolute left-[-21px] top-1/2 -translate-y-1/2 w-6 h-px ${styles.line}`}
                        />
                        <span
                          className={`absolute left-[-28px] top-1/2 -translate-y-1/2 w-4 h-4 rounded-full ${styles.dot}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {highlightMilestone && (
                <div className="mt-12 flex flex-col items-center gap-3">
                  <div className="relative flex items-center justify-center">
                    <span className="absolute left-[-250px] w-[250px] h-px bg-[#284B7A]/25" />
                    <span className="absolute right-[-250px] w-[250px] h-px bg-[#8C6B4A]/25" />
                    <div
                      className={`w-full max-w-[420px] px-6 sm:px-10 py-5 rounded-full bg-white3 border-4 border-[#1c3d72] flex flex-col items-center justify-center text-center shadow-lg transition-all duration-500 ${
                        visible ? "opacity-100 scale-100" : "opacity-0 scale-50"
                      }`}
                    >
                      <p className="text-xs tracking-[0.4em] text-black/60">
                        {highlightMilestone.year}
                      </p>
                      <p className="text-sm font-semibold text-[#1c3d72]">
                        {highlightMilestone.title}
                      </p>
                    </div>
                  </div>
                  <span className="text-[11px] uppercase tracking-[0.35em] text-black/40">
                    Unified into BSS Sonata Microcredit
                  </span>
                </div>
              )}

              <div className="relative mt-10 max-w-5xl mx-auto px-4 md:px-0">
                <span className="absolute left-6 md:left-1/2 md:-translate-x-1/2 top-0 bottom-0 w-[2px] bg-[#1c3d72]/70" />
                <div className="space-y-8 md:space-y-12">
                  {displayedMergedMilestones.map((item, index) => {
                    const isLeft = index % 2 === 0;
                    return (
                      <div
                        key={item.year}
                        className={`relative md:flex md:items-start md:gap-8 ${
                          isLeft ? "md:flex-row-reverse" : ""
                        }`}
                      >
                        <span
                          className={`absolute left-6 md:left-1/2 md:-translate-x-1/2 w-4 h-4 rounded-full ${
                            companyStyles[item.company].dot
                          } shadow-md transition-all duration-500 ${
                            visible
                              ? "opacity-100 scale-100"
                              : "opacity-0 scale-50"
                          }`}
                          style={{ transitionDelay: `${index * 120}ms` }}
                        />
                        <div
                          className={`pl-12 md:pl-0 ${
                            isLeft
                              ? "md:mr-[calc(50%+40px)] md:text-right"
                              : "md:ml-[calc(50%+40px)]"
                          }`}
                        >
                          <TimelineCard
                            item={item}
                            delay={index * 120}
                            isVisible={visible}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Mobile */}
            <MobileTimeline
              isVisible={visible}
              items={displayedMobileTimeline}
            />

            <div className="mt-12 bg-white3 rounded-[24px] px-6 py-4 text-center text-xs tracking-[0.35em] uppercase text-black/45 shadow-inner">
              Two legacies, one shared future in rural microfinance.
            </div>
          </div>
        </div>

        <div className="flex justify-center">
          <button
            onClick={() => setExpanded((prev) => !prev)}
            className="px-6 py-3 rounded-full border border-[#1c3d72] text-[#1c3d72] text-sm font-semibold tracking-[0.2em] uppercase bg-white hover:bg-[#1c3d72]/5 transition-colors"
            aria-expanded={expanded}
          >
            {expanded ? "Collapse Journey" : "Expand Journey"}
          </button>
        </div>
      </div>
    </section>
  );
}
